import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, Suspense } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import { FormattedMessage, FormattedNumber } from "react-intl";
import clsx from "clsx";
import { CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@material-ui/core";
import { useResource } from "@rest-hooks/core";
import { PriceOverviewResource } from "./PriceOverviewResource";
import { useCalculationFields } from "@ploy-lib/calculation";
const useStyles = makeStyles((theme) => ({
    table: {
        paddingLeft: 20,
        paddingRight: 20
    },
    title: {
        fontSize: "2.375rem",
        fontWeight: 700,
        lineHeight: 1.32,
        letterSpacing: "-.03rem",
        textAlign: "left"
    },
    currentPriceRow: { fontWeight: "bold" },
    header: { fontWeight: "bold" },
    transparent: { backgroundColor: "transparent" },
    tableCell: {
        padding: 5,
        borderBottom: "none"
    },
    tableHeader: {
        padding: 5,
        borderBottom: "none"
    },
    text: {
        textAlign: "left"
    }
}), { name: "DployPriceOverviewSection" });
addRegisteredSectionLayout({
    name: "PriceOverviewSection",
    displayName: "PriceOverviewSection",
    settings: {}
});
const PriceOverviewSection = (props) => {
    return (_jsx("div", { style: { textAlign: "center" }, children: _jsx(Suspense, { fallback: _jsx(CircularProgress, {}), children: _jsx(PriceOverviewSectionImpl, { ...props }) }) }));
};
const PriceOverviewSectionImpl = memo((props) => {
    const fields = [
        {
            namespace: "Calculator",
            name: "NominalInterestRate"
        },
        {
            namespace: "Calculator",
            name: "ActualInterestRate"
        },
        {
            namespace: "Calculator",
            name: "TotalTermsCountInYears"
        },
        {
            namespace: "Calculator",
            name: "NetLoanAmount"
        },
        {
            namespace: "Calculator",
            name: "Equity"
        },
        {
            namespace: "Calculator",
            name: "CreditPurchasePrice"
        }
    ];
    const calculationFields = useCalculationFields(...fields);
    const nominalInterest = Number(calculationFields[0]?.value ?? 0) / 100;
    const actualInterest = Number(calculationFields[1]?.value ?? 0) / 100;
    const termsInYears = Number(calculationFields[2]?.value ?? 0);
    const netLoanAmount = Number(calculationFields[3]?.value ?? 0);
    const equity = Number(calculationFields[4]?.value ?? 0);
    const creditPurchasePrice = Number(calculationFields[5]?.value ?? 0);
    const total = creditPurchasePrice - equity;
    const creditCost = total - netLoanAmount;
    // Param totalPrice is only here to trigger a re fetch on changes
    const { tableRows } = useResource(PriceOverviewResource.detail(), {
        totalPrice: total
    });
    const classes = useStyles();
    return (_jsxs(Grid, { container: true, spacing: 5, children: [_jsx(Grid, { item: true, xs: 1 }), _jsx(Grid, { item: true, xs: 10, children: _jsx(Typography, { variant: "h3", className: classes.title, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.title", defaultMessage: "Price Overiew", description: "Price overview section title" }) }) }), _jsx(Grid, { item: true, xs: 1 }), _jsx(PriceOverviewTable, { tableRows: tableRows, actualInterest: actualInterest }), _jsx(Grid, { item: true, xs: 1 }), _jsx(Grid, { item: true, xs: 10, children: _jsx(Typography, { variant: "h3", className: classes.title, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.subtitle", defaultMessage: "Price Example", description: "Price example title" }) }) }), _jsx(Grid, { item: true, xs: 1 }), _jsx(Grid, { item: true, xs: 1 }), _jsx(Grid, { item: true, xs: 10, children: _jsx(Typography, { className: classes.text, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.price_example", defaultMessage: "{netLoanAmount}, over {terms} years, nominell interest {nominalInterest}, actual interest {actualInterest}, cost {cost} total {total}.", description: "Price example", values: {
                            terms: termsInYears,
                            netLoanAmount: (_jsx(FormattedNumber, { value: netLoanAmount, format: "currency" })),
                            nominalInterest: (_jsx(FormattedNumber, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: "percent", value: nominalInterest })),
                            actualInterest: (_jsx(FormattedNumber, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: "percent", value: actualInterest })),
                            cost: _jsx(FormattedNumber, { value: creditCost, format: "currency" }),
                            total: _jsx(FormattedNumber, { value: total, format: "currency" })
                        } }) }) }), _jsx(Grid, { xs: 1 })] }));
});
const PriceOverviewTable = ({ tableRows, actualInterest }) => {
    const classes = useStyles();
    return (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 1 }), _jsx(Grid, { item: true, xs: 10, children: _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { className: clsx(classes.transparent, classes.tableHeader), children: _jsx(Typography, { className: classes.header, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.table.header.equity", defaultMessage: "Equity", description: "Header equity column" }) }) }), _jsx(TableCell, { className: clsx(classes.transparent, classes.tableHeader), children: _jsx(Typography, { className: classes.header, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.table.header.nominal_interest", defaultMessage: "Nominal interest", description: "Header nominal interest column" }) }) }), _jsx(TableCell, { className: clsx(classes.transparent, classes.tableHeader), children: _jsx(Typography, { className: classes.header, children: _jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.table.header.actual_interest", defaultMessage: "Actual interest", description: "Header actual interest column" }) }) })] }) }), _jsx(TableBody, { className: classes.transparent, children: tableRows?.map((row, i) => (_jsxs(TableRow, { children: [_jsx(TableCell, { className: clsx(classes.tableCell, {
                                                [classes.currentPriceRow]: row.currentPriceRow
                                            }), component: "th", scope: "row", children: row.lowEquity === 0 ? (_jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.table.header.equity-low", defaultMessage: "Under {highEquity} %", description: "Equity below high value", values: { highEquity: row.highEquity } })) : row.highEquity === 100 ? (_jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.table.header.equity-high", defaultMessage: "Over {lowEquity} %", description: "Equity above low value", values: { lowEquity: row.lowEquity } })) : (_jsx(FormattedMessage, { id: "ploy-ui.template-form.PriceOverviewSection.table.header.equity-from-to", defaultMessage: "{lowEquity} - {highEquity} %", description: "Equity between low and high values", values: {
                                                    lowEquity: row.lowEquity,
                                                    highEquity: row.highEquity
                                                } })) }), _jsx(TableCell, { className: clsx(classes.tableCell, {
                                                [classes.currentPriceRow]: row.currentPriceRow
                                            }), children: row.nominalInterest > 0 ? (_jsx(FormattedNumber, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: "percent", value: row.nominalInterest / 100 })) : ("-") }), _jsx(TableCell, { className: clsx(classes.tableCell, {
                                                [classes.currentPriceRow]: row.currentPriceRow
                                            }), children: row.currentPriceRow ? (_jsx(FormattedNumber, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: "percent", value: actualInterest })) : ("-") })] }, i))) })] }) }) }), _jsx(Grid, { item: true, xs: 1 })] }));
};
PriceOverviewSection.displayName = "DployPriceOverviewSection";
export { PriceOverviewSection };
//# sourceMappingURL=PriceOverviewSection.js.map